import { DataStatusBadge } from "@components/data-status-badge";
import {
  SHImage,
  SHStack,
  SHStackProps,
  SHTypography,
} from "@components/design-systems";
import EyeIconTooltip from "@components/svgs/eye-tooltip";
import SHIconTooltipContainer from "@components/design-systems/sh-icon-tooltip-container";
import { DateFormat } from "@constants/format";
import { useNotification } from "@hooks/useNotification";
import { useUserPermissions } from "@hooks/userUserPermission";
import { ProductDTO } from "@models/product/entities/product";
import LockOutlineIcon from "@mui/icons-material/LockOutlined";
import { useTheme } from "@mui/material";
import { hexToRGBA } from "@utils";
import { format } from "date-fns";
import { useMemo } from "react";

export interface ProductCardProps extends SHStackProps {
  product?: ProductDTO;
  onClickProduct: (productId?: string) => void;
  hasVisibility?: boolean;
  isRestrictedAccess?: boolean;
}
export const ProductCard = ({
  hasVisibility = false,
  isRestrictedAccess = false,
  product,
  onClickProduct,
  ...others
}: ProductCardProps) => {
  const { palette } = useTheme();
  const { notify } = useNotification();
  const { canViewProductStatus } = useUserPermissions();

  const cardBoxShadowColor = hexToRGBA(palette.common.black, 0.25);

  const isDisable = useMemo(() => {
    return isRestrictedAccess;
  }, [isRestrictedAccess]);

  const cardStyles = useMemo(
    () => ({
      position: "relative",
      gap: "16px",
      cursor: "pointer",
      padding: "20px 25px",
      backdropFilter: "blur(0px)",
      background: palette.common.white,
      border: `1px solid ${palette.secondary[100]}`,
      boxShadow: `3px 3px 4px ${cardBoxShadowColor}`,
      "&:hover": {
        boxShadow: "none",
        border: `1px solid ${palette.primary.dark}`,
      },
      "&:active": {
        boxShadow: `3px 3px 4px ${cardBoxShadowColor} "inset"`,
        border: `1px solid ${palette.primary.dark}`,
        backgroundColor: palette.secondary[50],
      },
    }),
    [palette, cardBoxShadowColor],
  );

  const handleCardClick = () => {
    if (isRestrictedAccess) {
      notify("This manager hasn't shared their profile with you.", {
        variant: "error",
        close: true,
      });
      return;
    }
    onClickProduct(product?.id);
  };

  const formatLastModified = useMemo(() => {
    return format(
      product?.lastModifiedDate ?? new Date(),
      `'Most recent update: ' ${DateFormat}`,
    );
  }, [product?.lastModifiedDate]);

  return (
    <>
      <SHStack
        width={{ lg: "32.75%", md: "49.33%", sm: "49.3%" }}
        spacing={2}
        direction="row"
        alignItems="center"
        sx={cardStyles}
        onClick={handleCardClick}
        {...others}
      >
        <SHStack
          direction="row"
          spacing={1}
          width={311}
          gap="15px"
          alignItems="center"
        >
          <SHImage
            src={product?.productLogo}
            width={67.1}
            height={67.1}
            style={{
              opacity: isDisable ? 0.8 : 1,
              filter: isDisable ? "grayscale(10%)" : "",
            }}
          />
          <SHStack textAlign="start">
            <SHTypography
              disabled={isDisable}
              variant="h2"
              fontSize={20}
              fontWeight={500}
              lineHeight="120%"
              sx={{ wordBreak: "break-word" }}
            >
              {product?.productName}
            </SHTypography>
            <SHTypography disabled={isDisable} variant="body4">
              {formatLastModified}
            </SHTypography>
            {canViewProductStatus(product?.id) && (
              <DataStatusBadge status={product?.dataStatus} />
            )}
          </SHStack>
        </SHStack>
        <EyeIconTooltip
          isVisible={hasVisibility}
          tooltipProps={{
            title: "Visibility limited to certain user groups",
            placement: "top",
            arrow: true,
          }}
          isActive={false}
          iconProps={{
            style: {
              height: 20,
              width: 20,
              position: "absolute",
              top: 0,
              right: 0,
              padding: "8px",
            },
          }}
        />
        <SHIconTooltipContainer isVisible={isRestrictedAccess} tooltipProps={{
            title: "This manager hasn't shared their profile with you.",
            placement: "top",
            arrow: true,
          }}>
          <LockOutlineIcon
            style={{
              height: 20,
              width: 20,
              position: "absolute",
              top: 0,
              right: 0,
              padding: "8px",
              color: palette.grey[500],
            }}
          />
        </SHIconTooltipContainer>
      </SHStack>
    </>
  );
};
